import type { CSSProperties } from 'react';
import Link from 'next/link';
import { Trans, useTranslation } from 'next-i18next';
import { ExclamationmarkSmallIcon, JellyIcon } from '@/icons';
import type { AvatarShapePart } from '@/shared/features/user-avatar';
import { cn } from '@/utils/tailwind.util';

/**
 * Figma 1506:37052 기준 카피.
 * defaultValue 는 S3 locale 캐시 갱신 전까지의 안전한 폴백이다.
 */
export const AVATAR_PURCHASE_COPY = {
  confirmTitle: {
    key: 'common:avatarDecorate.purchaseDialogTitle',
    defaultValue: '아이템을 구매할까요?'
  },
  insufficientTitle: {
    key: 'common:avatarDecorate.insufficientTitle',
    defaultValue: '젤리가 부족합니다'
  },
  purchaseAction: {
    key: 'common:avatarDecorate.purchaseAction',
    defaultValue: '구매'
  },
  myPoints: {
    key: 'common:avatarDecorate.myPointsLabel',
    defaultValue: '내 보유 젤리'
  },
  itemPrice: {
    key: 'common:avatarDecorate.itemPriceLabel',
    defaultValue: '아이템 가격'
  },
  // 중간에 '내 클래스' 링크가 끼는 문장 — 분할 키 대신 단일 키 + <Trans> 로.
  insufficientHelp: {
    key: 'common:avatarDecorate.insufficientHelp',
    defaultValue: '부족한 젤리는 <ClassLink>내 클래스</ClassLink>에서 받을 수 있어요'
  }
} as const;

// 정보 박스 한 줄(라벨 ↔ 값). 값 색만 상황에 따라 바뀐다.
const InfoRow = ({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) => <div className="flex h-[26px] items-center justify-between pl-4xs pr-3xs" data-sentry-component="InfoRow" data-sentry-source-file="AvatarPurchaseConfirmBody.tsx">
    <span className="text-body-md-semibold text-neutral">{label}</span>
    <span className="flex shrink-0 items-center gap-2xs">{children}</span>
  </div>;
const COLOR_SWATCH_LIMIT = 5;
const EMPTY_COLOR_PARTS: AvatarShapePart[] = [];

/**
 * 아이템 구매 확인 / 포인트 부족 모달 본문 (Figma 1506:37052).
 *
 * 프리뷰 카드 → 정보 박스 순서. 두 상태가 같은 레이아웃을 쓰되:
 * - 구매 확인: 가격 → 내 보유 포인트. 보유 포인트 값은 strong.
 * - 포인트 부족: 가격 → 내 보유 포인트. 보유 포인트 값은 destructive.
 */
export const AvatarPurchaseConfirmBody = ({
  previewUrl,
  colorParts = EMPTY_COLOR_PARTS,
  price,
  balance,
  itemPriceLabel,
  isNew = false,
  isHot = false,
  isInsufficient = false,
  insufficientHelpHref,
  onInsufficientHelpClick
}: {
  previewUrl: string;
  colorParts?: AvatarShapePart[];
  price: number;
  balance: number;
  itemPriceLabel?: string;
  isNew?: boolean;
  isHot?: boolean;
  isInsufficient?: boolean;
  insufficientHelpHref?: string;
  onInsufficientHelpClick?: () => void;
}) => {
  const {
    t
  } = useTranslation(['common']);
  const visibleColorParts = colorParts.slice(0, COLOR_SWATCH_LIMIT);
  const shouldShowColorParts = colorParts.length > 1;
  return <div className="flex flex-col gap-sm" data-sentry-component="AvatarPurchaseConfirmBody" data-sentry-source-file="AvatarPurchaseConfirmBody.tsx">
      <div className="flex flex-col gap-[2px] overflow-hidden rounded-xs border border-opacity-black-2 bg-default">
        <div className={cn('relative flex w-full flex-col items-center justify-center overflow-hidden bg-neutral/50 pt-sm', shouldShowColorParts ? 'gap-xs pb-lg' : 'pb-xs')}>
          <img src={previewUrl} alt="" onError={event => {
          event.currentTarget.style.visibility = 'hidden';
        }} className="size-[80px] object-contain" />
          {(isNew || isHot) && <span className={cn('absolute right-[-1px] top-[-1px] z-[1] rounded-bl-xs px-3xs py-4xs text-caption-md-semibold text-inverse shadow-sm', isHot ? 'bg-gradient-to-r from-[#f268f5] to-[#ff737b]' : 'bg-gradient-to-r from-[#18d0b4] to-[#6ebfff]')}>
              {isHot ? 'HOT' : 'NEW'}
            </span>}
          {shouldShowColorParts && <div className="flex items-start gap-3xs">
              {visibleColorParts.map(part => <span key={part.partId} className="size-[10px] shrink-0 rounded-2xs border border-opacity" style={{
            '--avatar-purchase-color': part.hex ?? '#B8B9C8',
            backgroundColor: 'var(--avatar-purchase-color)'
          } as CSSProperties} />)}
            </div>}
        </div>

        <div className="flex w-full flex-col gap-xs bg-default p-md">
          <InfoRow label={itemPriceLabel ?? t(AVATAR_PURCHASE_COPY.itemPrice.key, {
          defaultValue: AVATAR_PURCHASE_COPY.itemPrice.defaultValue
        })} data-sentry-element="InfoRow" data-sentry-source-file="AvatarPurchaseConfirmBody.tsx">
            <JellyIcon size="sm" data-sentry-element="JellyIcon" data-sentry-source-file="AvatarPurchaseConfirmBody.tsx" />
            <span className="text-body-lg-bold text-neutral">{price}</span>
          </InfoRow>
          <InfoRow label={t(AVATAR_PURCHASE_COPY.myPoints.key, {
          defaultValue: AVATAR_PURCHASE_COPY.myPoints.defaultValue
        })} data-sentry-element="InfoRow" data-sentry-source-file="AvatarPurchaseConfirmBody.tsx">
            <JellyIcon size="sm" data-sentry-element="JellyIcon" data-sentry-source-file="AvatarPurchaseConfirmBody.tsx" />
            <span className={cn('text-body-lg-bold',
          // Figma 1507-37821: 내 보유 젤리 값은 purple-600. 부족 시엔 경고로 destructive 유지.
          isInsufficient ? 'text-destructive' : 'text-purple-600')}>
              {balance}
            </span>
          </InfoRow>
        </div>
      </div>

      {isInsufficient && insufficientHelpHref && <div className="flex w-full items-center justify-center rounded-sm bg-opacity-black-1 px-md py-xs text-center text-body-sm-medium text-alternative">
          <ExclamationmarkSmallIcon size="sm" className="mr-3xs shrink-0 text-alternative" />
          <span>
            <Trans t={t} i18nKey={AVATAR_PURCHASE_COPY.insufficientHelp.key} defaults={AVATAR_PURCHASE_COPY.insufficientHelp.defaultValue} components={{
          ClassLink: <Link href={insufficientHelpHref} className="text-link" onClick={onInsufficientHelpClick} />
        }} />
          </span>
        </div>}
    </div>;
};